import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {TagIcon} from '@primer/octicons-react'
import {Fragment} from 'react'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {UnlabeledEventNode} from './issue-viewer-view-query-types'
import {Label} from './Label'
import type {LabeledEventFragment} from './LabeledEvent'
import styles from './labels.module.css'
import {RolledupLabeledEvent} from './RolledupLabeledEvent'
import {TimelineRow} from './row/TimelineRow'

// RELAY-CLEANUP(UnlabeledEvent): widens nullable fields so still-Relay parent data is assignable.
// Remove when the timeline pagination fragments leave Relay.
export type UnlabeledEventFragment = RelayCompat<
  Pick<UnlabeledEventNode, 'actor' | 'createdAt' | 'databaseId' | 'label'>
>

type UnlabeledEventProps = {
  queryRef: UnlabeledEventFragment
  rollupGroup?: Record<string, Array<LabeledEventFragment | UnlabeledEventFragment>>
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  timelineEventBaseUrl: string
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
}

export function UnlabeledEvent({
  queryRef,
  rollupGroup,
  issueUrl,
  onLinkClick,
  timelineEventBaseUrl,
  highlightedEventId,
  refAttribute,
}: UnlabeledEventProps): React.ReactElement {
  const {actor, createdAt, databaseId} = queryRef
  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={TagIcon}
    >
      <TimelineRow.Main>
        {rollupGroup ? (
          <RolledupLabeledEvent rollupGroup={rollupGroup} timelineEventBaseUrl={timelineEventBaseUrl} />
        ) : (
          <UnlabeledRendering queryRefs={[queryRef]} timelineEventBaseUrl={timelineEventBaseUrl} />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

export const UnlabeledRendering = ({
  queryRefs,
  timelineEventBaseUrl,
}: Pick<UnlabeledEventProps, 'timelineEventBaseUrl'> & {queryRefs: UnlabeledEventFragment[]}) => {
  if (queryRefs.length === 0) {
    return null
  }

  return (
    <>
      {`${LABELS.timeline.removed} `}
      {queryRefs.map((queryRef, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Fragment key={index}>
          <InternalAddedUnlabelRendering queryRef={queryRef} timelineEventBaseUrl={timelineEventBaseUrl} />{' '}
        </Fragment>
      ))}
    </>
  )
}

const InternalAddedUnlabelRendering = ({
  queryRef,
  timelineEventBaseUrl,
}: {
  queryRef: UnlabeledEventFragment
  timelineEventBaseUrl: string
}) => {
  const {label} = queryRef
  return (
    <div className={styles.labelContainer}>
      <Label label={label} timelineEventBaseUrl={timelineEventBaseUrl} />
    </div>
  )
}
